/**
 * Render an arbitrary error (RTK Query, fetch, JS exception, anything
 * thrown by the save/hydration pipeline) into a single human-readable
 * line that can land on a wizard error alert.
 *
 * The bar is "never just a red box" — every failure should tell the
 * operator what went wrong, including HTTP status, server-side message,
 * and a sketch of the response body when the backend sent one. RTK
 * Query's error shapes (FetchBaseQueryError / SerializedError) are plain
 * objects, not `Error` instances, so `err instanceof Error` misses them
 * and previously fell through to a bare "Unknown error".
 */
export function describeError(err: unknown): string {
  if (err === null || err === undefined) return "Unknown error";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const e = err as Record<string, unknown>;
    // RTK Query FetchBaseQueryError — has either a numeric `status` (HTTP)
    // or a string `status` token (e.g. "FETCH_ERROR", "PARSING_ERROR").
    if (e.status !== undefined) {
      const statusLabel = typeof e.status === "number" ? `HTTP ${e.status}` : String(e.status);
      // Server-side errors typically come back as { message: "…" } or
      // { status: "error", message: "…" }. Plain-string bodies and any
      // other shape get JSON-stringified so the operator at least sees
      // the wire bytes instead of "[object Object]".
      const data = e.data;
      let dataDesc: string;
      if (typeof data === "string") {
        dataDesc = data;
      } else if (data && typeof data === "object" && "message" in data) {
        dataDesc = String((data as Record<string, unknown>).message);
      } else if (data !== undefined) {
        try {
          dataDesc = JSON.stringify(data);
        } catch {
          dataDesc = "<unserializable response body>";
        }
      } else if (typeof e.error === "string") {
        // FetchBaseQueryError uses `.error` (string) for non-HTTP cases.
        dataDesc = e.error;
      } else {
        dataDesc = "(no response body)";
      }
      return `${statusLabel} — ${dataDesc}`;
    }
    // SerializedError shape (RTK Query catches sometimes wrap as this).
    if (typeof e.message === "string") {
      return e.code !== undefined ? `${e.code}: ${e.message}` : (e.message as string);
    }
    // Native Error / unknown object — last-ditch JSON dump.
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }
  return String(err);
}

export default describeError;
